import Linkify from 'react-linkify';
import { Typography, TypographyProps, TypographyVariant } from '@material-ui/core';
import { Builder } from '@builder.io/react';
import { isEmpty } from 'lodash';
import React, { ElementType, useState } from 'react';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { builderTypographyInputs } from './BuilderTypographyInputs';
import DynamicValueWrapper from '../DynamicValueWrapper/DynamicValueWrapper';

const UNDERLINE_FORMATTING_STYLES = {
  textDecoration: 'underline',
  textDecorationColor: 'inherit',
  textDecorationThickness: '1px',
  textUnderlineOffset: '2px',
};

const isHeader = (variant: string) =>
  variant === 'h1' ||
  variant === 'h2' ||
  variant === 'h3' ||
  variant === 'h4' ||
  variant === 'h5' ||
  variant === 'h6';

export enum TypographyWeight {
  Bold = 'bold',
  Medium = 500,
}

export enum TypographyFormat {
  Default = 'DEFAULT',
  Underlined = 'UNDERLINED',
  HoverUnderlined = 'HOVER_UNDERLINED',
}

export interface TypographyWrapperProps {
  variant: TypographyVariant;
  format?: TypographyFormat;
  weight?: TypographyWeight;
  typographyOverrides?: TypographyProps;
  children: React.ReactNode;
  component?: ElementType<any>;
  id?: string;
  // If true, the text will be wrapped in a DynamicValueWrapper to exclude from translation in Weglot. Should be used to exclude variables such as product data and user data.
  isDynamicValue?: boolean;
  disableLinkify?: boolean;
}

// If we are using Shop Config V2's CSS, wrap the text in the correct Typography variant
const TypographyWrapper: React.FC<TypographyWrapperProps> = ({
  variant,
  typographyOverrides,
  children,
  format,
  weight,
  component,
  id,
  isDynamicValue,
  disableLinkify,
}) => {
  const { style: styleOverrides, ...rest } = typographyOverrides || {};

  const [isHovered, setIsHovered] = useState(false);

  const isHeaderVariant = isHeader(variant);
  let formattingStyles: CSSProperties = isHeaderVariant ? { textWrap: 'balance' } : {};

  if (format === TypographyFormat.Underlined) {
    formattingStyles = { ...formattingStyles, ...UNDERLINE_FORMATTING_STYLES };
  }

  if (format === TypographyFormat.HoverUnderlined) {
    formattingStyles = {
      ...formattingStyles,
      ...(isHovered && UNDERLINE_FORMATTING_STYLES),
    };
  }

  if (weight) {
    formattingStyles = { ...formattingStyles, fontWeight: weight };
  }

  const hasStyleOverrides = !isEmpty(formattingStyles) || !isEmpty(styleOverrides);
  const propOverrides = {
    ...(component && { component }),
    ...rest,
    ...(hasStyleOverrides && { style: { ...formattingStyles, ...styleOverrides } }),
  };

  const formattedTypography = (
    <Typography
      id={id}
      variant={variant}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...propOverrides}
    >
      {isDynamicValue ? <DynamicValueWrapper>{children}</DynamicValueWrapper> : children}
    </Typography>
  );

  // react-linkify is not supported in SSR, so we need to render the typography directly
  const isSSR = typeof window === 'undefined';

  if (isSSR || disableLinkify) {
    return formattedTypography;
  }

  return (
    <Linkify
      componentDecorator={(href: string, text: string, key: string) => (
        <a id={id} key={key} href={href} style={{ color: 'inherit' }}>
          <Typography
            variant={variant}
            display="inline"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={UNDERLINE_FORMATTING_STYLES}
            {...propOverrides}
          >
            {isDynamicValue ? <DynamicValueWrapper>{text}</DynamicValueWrapper> : text}
          </Typography>
        </a>
      )}
    >
      {formattedTypography}
    </Linkify>
  );
};

Builder.registerComponent(TypographyWrapper, {
  name: 'TypographyWrapper',
  inputs: builderTypographyInputs,
});

export default TypographyWrapper;
